import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Workflow } from "lucide-react";
import { PostEditor } from "./PostEditor";
import { WorkflowWizard } from "./WorkflowWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("nyla_admin_token")}` });

export const PostsManager = ({ onUnauthorized }) => {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () =>
    axios
      .get(`${API}/admin/blog`, { headers: auth() })
      .then((r) => setPosts(r.data))
      .catch((e) => (e.response?.status === 401 ? onUnauthorized() : toast.error("Could not load posts")));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${post.id}`, { headers: auth() });
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (editing !== null) {
    const isWizard = editing === "new" || (editing?.workflow?.stages && editing?.status === "draft");
    if (isWizard) {
      return (
        <WorkflowWizard
          post={editing === "new" ? null : editing}
          onUnauthorized={onUnauthorized}
          onDone={() => {
            setEditing(null);
            load();
          }}
        />
      );
    }
    return (
      <PostEditor
        post={editing}
        onDone={(saved) => {
          setEditing(null);
          if (saved) load();
        }}
      />
    );
  }

  return (
    <div data-testid="posts-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-1">Blog Posts</h1>
          <p className="text-sm text-[#86868B]">Write, edit, and publish articles for The Nyla Journal.</p>
        </div>
        <button
          data-testid="new-post-button"
          onClick={() => setEditing("new")}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1D1D1F] text-white text-sm font-medium hover:bg-black active:scale-95 transition-transform duration-200"
        >
          <Workflow className="w-4 h-4" /> New Post — Guided Workflow
        </button>
      </div>

      {!posts ? (
        <p className="text-sm text-[#86868B]">Loading…</p>
      ) : (
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] divide-y divide-[#D2D2D7]/50">
          {posts.map((p) => (
            <div key={p.id} data-testid={`post-row-${p.slug}`} className="flex items-center gap-4 px-6 py-4">
              {p.image ? (
                <img src={p.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-[#F5F5F7] shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-[#F5F5F7] shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{p.title}</p>
                <p className="text-xs text-[#86868B]">{p.category} · {p.published_at}</p>
              </div>
              <span
                data-testid={`post-status-${p.slug}`}
                className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                  p.status === "published" ? "bg-[#2C3E35]/10 text-[#2C3E35]" : "bg-amber-100 text-amber-700"
                }`}
              >
                {p.status}
              </span>
              <button data-testid={`post-edit-${p.slug}`} onClick={() => setEditing(p)} className="p-2 rounded-full hover:bg-black/5" aria-label="Edit">
                <Pencil className="w-4 h-4 text-[#86868B]" />
              </button>
              <button data-testid={`post-delete-${p.slug}`} onClick={() => remove(p)} className="p-2 rounded-full hover:bg-red-50" aria-label="Delete">
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          ))}
          {posts.length === 0 && <p className="px-6 py-10 text-sm text-[#86868B] text-center">No posts yet — write your first one.</p>}
        </div>
      )}
    </div>
  );
};
